import {
  labelConfigSelector,
  updateLabelConfig,
  labelPropertiesSelector,
  onLabelPropertyValueChange,
  isLabelPropertyEnabledSelector,
} from '../../../data/labelInfos/index.js'
import ConfigSelect from './ConfigSelect.js'
import {getState, useSelector} from '../../../store.js'
import {
  DHL,
  FEDEX,
  FEDEX_360,
  SHIPPO,
  UPS,
} from '../../../common/constants/ShipperNames.js'
import ConfigTextInput from './ConfigTextInput.js'
import useLabelProperty, {
  ADDITIONAL_SHIPPER_DEPENDENT_PROPERTIES_FUNC,
  defaultValidLabelPropertiesFunc,
} from './useLabelProperty.js'
import EnableLabelProperty, {PickLabelProperty} from './EnableLabelProperty.js'
import {useLabelConfigContext} from '../LabelConfigContext.js'

// Type
const VALID_SHIPPER_TYPES = [DHL, FEDEX, FEDEX_360, SHIPPO, UPS]

export function getPaymentTypeLabelProperty(shipperType) {
  return `${shipperType}__payment_type`
}

function labelPropertiesFunc(shipperType) {
  return VALID_SHIPPER_TYPES.includes(shipperType)
    ? [getPaymentTypeLabelProperty(shipperType)]
    : []
}

// Account
function getPaymentAccountLabelProperty(shipperType) {
  return `${shipperType}__payment_account`
}
const PAYMENT_ACCOUNT_VALID_LABEL_PROPERTIES = VALID_SHIPPER_TYPES.map(
  getPaymentAccountLabelProperty,
)

function showPaymentAccountSelector(state, {labelInfoID, shipperType}) {
  const labelConfig = labelConfigSelector(state, {labelInfoID})
  const [labelProperty] = labelPropertiesFunc(shipperType)

  const paymentType = labelConfig[labelProperty] || ''

  return (
    (paymentType !== 'S' && shipperType === DHL) ||
    (paymentType !== 'SENDER' &&
      [FEDEX, FEDEX_360, SHIPPO].includes(shipperType)) ||
    (paymentType !== 'BillShipper' && shipperType === UPS)
  )
}

function paymentAccountDependentFunc(shipperType) {
  return [
    ...ADDITIONAL_SHIPPER_DEPENDENT_PROPERTIES_FUNC(shipperType),
    ...labelPropertiesFunc(shipperType),
  ]
}

function paymentAccountPropertiesFunc(shipperType, labelInfoID) {
  const showPaymentAccount = showPaymentAccountSelector(getState(), {
    labelInfoID,
    shipperType,
  })

  return showPaymentAccount ? [getPaymentAccountLabelProperty(shipperType)] : []
}

// Zip and Country
const ZIP_COUNTRY_VALID_SHIPPER_TYPES = [UPS, SHIPPO]
function getPaymentZipLabelProperty(shipperType) {
  return `${shipperType}__payment_zip`
}
function getPaymentCountryLabelProperty(shipperType) {
  return `${shipperType}__payment_country`
}
const ZIP_COUNTRY_VALID_LABEL_PROPERTIES = [
  ...ZIP_COUNTRY_VALID_SHIPPER_TYPES.map(getPaymentZipLabelProperty),
  ...ZIP_COUNTRY_VALID_SHIPPER_TYPES.map(getPaymentCountryLabelProperty),
]

function showPaymentZipCountrySelector(state, {labelInfoID, shipperType}) {
  const labelConfig = labelConfigSelector(state, {labelInfoID})
  const [labelProperty] = labelPropertiesFunc(shipperType)

  const paymentType = labelConfig[labelProperty] || ''

  return (
    (paymentType === 'BillThirdParty' && shipperType === UPS) ||
    (['THIRD_PARTY', 'THIRD_PARTY_CONSIGNEE'].includes(paymentType) &&
      shipperType === SHIPPO)
  )
}

function isZipCountryPropertiesEnabledSelector(
  state,
  {labelInfoID, shipperType},
) {
  const properties = labelPropertiesSelector(state, {labelInfoID})

  return (
    properties.includes(getPaymentZipLabelProperty(shipperType)) &&
    properties.includes(getPaymentCountryLabelProperty(shipperType))
  )
}

function zipCountryDependentPropertiesFunc(shipperType) {
  return [
    ...ADDITIONAL_SHIPPER_DEPENDENT_PROPERTIES_FUNC(shipperType),
    ...labelPropertiesFunc(shipperType),
  ]
}

function zipCountryLabelPropertiesFunc(shipperType, labelInfoID) {
  const showPaymentZipCountry = showPaymentZipCountrySelector(getState(), {
    labelInfoID,
    shipperType,
  })

  return showPaymentZipCountry
    ? [
        getPaymentZipLabelProperty(shipperType),
        getPaymentCountryLabelProperty(shipperType),
      ]
    : []
}

export function Payor() {
  const {labelInfoID, shipperType, onChange} = useLabelConfigContext()
  const [propertySpecs] = Payor.propertySpecList
  const [labelProperty] = propertySpecs.labelPropertiesFunc(
    shipperType,
    labelInfoID,
  )

  return (
    <ConfigSelect
      label="Payor"
      noEmptyOption
      onChange={(value) => {
        updateLabelConfig(labelInfoID, {
          [labelProperty]: value,
        })

        onLabelPropertyValueChange(labelInfoID, labelProperty)

        onChange()
      }}
      labelProperty={labelProperty}
      propertySpecs={propertySpecs}
      shipperOptionsProperty="payor"
    />
  )
}

Payor.propertySpecList = [
  {
    validShipperTypes: VALID_SHIPPER_TYPES,
    labelPropertiesFunc,
    dependentPropertiesFunc: ADDITIONAL_SHIPPER_DEPENDENT_PROPERTIES_FUNC,
    validLabelPropertiesFunc: defaultValidLabelPropertiesFunc,
  },
]

export function PayorAccountNumber() {
  const {labelInfoID, shipperType, onChange} = useLabelConfigContext()
  const [propertySpecs] = PayorAccountNumber.propertySpecList
  const [labelProperty] = propertySpecs.labelPropertiesFunc(
    shipperType,
    labelInfoID,
  )
  const showPaymentAccount = useSelector((state) =>
    showPaymentAccountSelector(state, {labelInfoID, shipperType}),
  )

  return (
    <ConfigTextInput
      label="Payor Account Number"
      onChange={(value) => {
        updateLabelConfig(labelInfoID, {
          [labelProperty]: value,
        })

        onChange()
      }}
      labelProperty={labelProperty}
      propertySpecs={propertySpecs}
      hide={!showPaymentAccount}
    />
  )
}

PayorAccountNumber.propertySpecList = [
  {
    validShipperTypes: VALID_SHIPPER_TYPES,
    labelPropertiesFunc: paymentAccountPropertiesFunc,
    dependentPropertiesFunc: paymentAccountDependentFunc,
    validLabelPropertiesFunc() {
      return PAYMENT_ACCOUNT_VALID_LABEL_PROPERTIES
    },
  },
]

export function PayorZipcode() {
  const {
    labelInfoID,
    shipperType,
    onChange,
    isLabelEnableMode,
    isSummary,
    isPickMode,
  } = useLabelConfigContext()
  const [propertySpecs] = PayorZipcode.propertySpecList
  const [paymentZipLabelProperty, paymentCountryLabelProperty] =
    propertySpecs.labelPropertiesFunc(shipperType, labelInfoID)

  const labelConfig = useSelector((state) =>
    labelConfigSelector(state, {labelInfoID}),
  )
  const showPaymentZipCountry = useSelector((state) =>
    showPaymentZipCountrySelector(state, {labelInfoID, shipperType}),
  )

  useLabelProperty({
    labelProperty: paymentZipLabelProperty,
    propertySpecs,
  })

  const isLabelPropertyEnabled = useSelector((state) =>
    isLabelPropertyEnabledSelector(state, {
      labelInfoID,
      labelProperty: paymentZipLabelProperty,
    }),
  )
  const paymentZip = labelConfig[paymentZipLabelProperty] || ''
  const paymentCountry = labelConfig[paymentCountryLabelProperty] || ''

  if (!showPaymentZipCountry) {
    return null
  }

  const label = 'Payor Zipcode/Country'

  if (isSummary) {
    return label
  }

  if (isPickMode) {
    return (
      <PickLabelProperty
        label={label}
        labelProperty={paymentZipLabelProperty}
      />
    )
  }

  return (
    <li className="list__item--shipping-options fs-n1 margin-bottom-5">
      <div className="flex flex-wrap wrap--edit-preset-form-input">
        <div className="margin-bottom-5 margin-right-15">
          <label className="label--bold" htmlFor="payment-zip">
            Payor Zipcode
          </label>
          <input
            className="input--md inline-block"
            type="text"
            id="payment-zip"
            value={paymentZip}
            onChange={(event) => {
              updateLabelConfig(labelInfoID, {
                [paymentZipLabelProperty]: event.target.value,
              })

              onChange()
            }}
            disabled={isLabelEnableMode && !isLabelPropertyEnabled}
          />
        </div>
        <div className="margin-bottom-5">
          <label className="label--bold" htmlFor="payment-country">
            Payor Country Code
          </label>
          <input
            className="input--sm inline-block"
            type="text"
            id="payment-country"
            value={paymentCountry}
            onChange={(event) => {
              updateLabelConfig(labelInfoID, {
                [paymentCountryLabelProperty]: event.target.value,
              })

              onChange()
            }}
            disabled={isLabelEnableMode && !isLabelPropertyEnabled}
          />
        </div>
      </div>
      <EnableLabelProperty
        labelProperty={paymentZipLabelProperty}
        checkedSelector={(state) =>
          isZipCountryPropertiesEnabledSelector(state, {
            labelInfoID,
            shipperType,
          })
        }
      />
    </li>
  )
}

PayorZipcode.propertySpecList = [
  {
    validShipperTypes: ZIP_COUNTRY_VALID_SHIPPER_TYPES,
    labelPropertiesFunc: zipCountryLabelPropertiesFunc,
    dependentPropertiesFunc: zipCountryDependentPropertiesFunc,
    validLabelPropertiesFunc() {
      return ZIP_COUNTRY_VALID_LABEL_PROPERTIES
    },
  },
]
